/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, NgZone, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import * as _ from 'lodash';
import template from './external-demand-source-analysis-page.component.html';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-external-demand-source-analysis-page',
  template
})
export class ExternalDemandSourceAnalysisPageComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;

  effectiveIsNew = false;
  canSeeReports = false;
  isPrebid = true;
  bidders: Array<{bidder: string; biddersHook: any}> = [];
  allBidders = '';
  statsData: any = null;
  selBidder: string = null;
  oneFieldHook: any = {};
  biddersHook: any;

  private subscribedModelComms: any;
  private modelCommsUnsub: (() => void) | null = null;

  constructor(
    @Inject(LocalUserPermissions) LocalUserPermissions: any,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.canSeeReports = LocalUserPermissions.getAnyVisibility().RUN_REPORTS === true;
    this.biddersHook = this.createBiddersHook();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model || changes.isNew) {
      this.resetForModel();
    }
    if (changes.modelComms) {
      this.subscribeModelComms();
    }
  }

  ngOnDestroy(): void {
    if (this.modelCommsUnsub) {
      this.modelCommsUnsub();
    }
  }

  trackByBidder(_index: number, bidder: {bidder: string}): string {
    return bidder.bidder;
  }

  private createBiddersHook(): any {
    return {
      onBiddersFound: (bidders: string[], stats: any) => {
        if (!_.isArray(bidders) || bidders.length === 0) {
          return;
        }
        this.bidders = _.map(bidders, (bidder) => ({
          bidder,
          biddersHook: {}
        }));
        this.biddersHook.biddersRegistered = true;
        this.allBidders = bidders.join(', ');
        this.statsData = stats;
        this.selBidder = this.bidders[0].bidder;
        this.oneFieldHook = {};
      },
      onSelections: (stats: any, dateOption: any, startDate: any, endDate: any, countType: any, yAxisOption: any, selFieldIds: any, selFieldId: any) => {
        // Keep parent stats in sync so *ngIf-mounted and later-selected bidder charts
        // pick up date/field changes via [statsData], not only via reboot.
        this.statsData = stats;
        _.forEach(this.bidders, (bidderEntry) => {
          if (!_.isFunction(_.get(bidderEntry, 'biddersHook.reboot'))) {
            return;
          }
          bidderEntry.biddersHook.reboot(stats, dateOption, startDate, endDate, countType, yAxisOption, selFieldIds, selFieldId);
        });
        if (_.isFunction(_.get(this.oneFieldHook, 'reboot'))) {
          this.oneFieldHook.reboot(stats, dateOption, startDate, endDate, countType, yAxisOption, selFieldIds, selFieldId);
        }
        this.cdr.detectChanges();
      }
    };
  }

  private resetForModel(): void {
    this.isPrebid = this.model?.externalPartner === 'PREBID_JS';
    this.bidders = [];
    this.allBidders = '';
    this.statsData = null;
    this.selBidder = null;
    this.oneFieldHook = {};
    this.biddersHook = this.createBiddersHook();
  }

  private subscribeModelComms(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }
    if (this.modelCommsUnsub) {
      this.modelCommsUnsub();
    }
    this.subscribedModelComms = this.modelComms;
    // Route isNew never flips after save; modelComms does.
    this.modelCommsUnsub = this.modelComms.addSubscriber((data: any, type: any, isNewFromComms: boolean) => {
      if (type || !data?.name) {
        return;
      }
      this.ngZone.run(() => {
        this.model = _.cloneDeep(data);
        if (isNewFromComms === true || isNewFromComms === false) {
          this.effectiveIsNew = isNewFromComms;
        } else {
          this.effectiveIsNew = false;
        }
        this.resetForModel();
      });
    });
  }
}
